import View from 'ol/View';
import { fromLonLat } from 'ol/proj';
import { boundingExtent, createEmpty, extend } from 'ol/extent';

import {
  AreaEntity,
  TaskEntity,
  ZoneEntity
} from '../../types/game-document/entities';

// reposition all objects from map to illustration image
export const mapCoordToImageCoord = (
  coord: [number, number],
  mapExtent: [number, number, number, number],
  imgWidth: number,
  imgHeight: number
): [number, number] => {
  const [minX, minY, maxX, maxY] = mapExtent;

  const sx = imgWidth / (maxX - minX);
  const sy = imgHeight / (maxY - minY);

  const x = (coord[0] - minX) * sx;
  const y = (coord[1] - minY) * sy;

  return [x, y];
};

export const getAllObjectsExtent = (
  data: any,
  buffer = 50
): [number, number, number, number] => {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  data.tasks?.forEach((task: any) => {
    if (task?.boundary?.geometry?.type === 'Point') {
      const [x, y] = task.boundary.geometry.coordinates;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  });

  data.areas?.forEach((area: any) => {
    const g = area?.boundary?.geometry;
    if (g?.type === 'Polygon') {
      g.coordinates.flat(1).forEach(([x, y]: [number, number]) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });
    }
  });

  data.zones?.forEach((zone: any) => {
    const g = zone?.boundary?.geometry;
    if (g?.type === 'Polygon') {
      g.coordinates.flat(1).forEach(([x, y]: [number, number]) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });
    }
  });

  if (minX === Infinity) return [0, 0, 0, 0];

  if (minX === maxX) {
    minX -= buffer;
    maxX += buffer;
  }
  if (minY === maxY) {
    minY -= buffer;
    maxY += buffer;
  }

  return [minX, minY, maxX, maxY];
};

export const padExtent = (
  extent: [number, number, number, number],
  paddingFraction: number
): [number, number, number, number] => {
  const [minX, minY, maxX, maxY] = extent;
  const width = maxX - minX;
  const height = maxY - minY;

  const padX = width * paddingFraction;
  const padY = height * paddingFraction;

  return [minX - padX, minY - padY, maxX + padX, maxY + padY];
};

export const repositionAllToIllustration = (
  data: any,
  imgWidth: number,
  imgHeight: number,
  paddingFraction = 0.05
) => {
  let extent = getAllObjectsExtent(data);
  extent = padExtent(extent, paddingFraction);

  const toImg = (c: [number, number]) =>
    mapCoordToImageCoord(c, extent, imgWidth, imgHeight);

  const tasks = data.tasks?.map((task: any) => {
    if (task?.boundary?.geometry?.type === 'Point') {
      task.boundary.geometry.coordinates = toImg(
        task.boundary.geometry.coordinates as [number, number]
      );
    }

    return task;
  }) as TaskEntity[];

  const areas = data.areas?.map((area: any) => {
    const g = area?.boundary?.geometry;
    if (g?.type === 'Polygon') {
      g.coordinates = g.coordinates.map((ring: [number, number][]) =>
        ring.map(toImg)
      );
    }

    return area;
  }) as AreaEntity[];

  const zones = data.zones?.map((zone: any) => {
    const g = zone?.boundary?.geometry;
    if (g?.type === 'Polygon') {
      g.coordinates = g.coordinates.map((ring: [number, number][]) =>
        ring.map(toImg)
      );
    }

    return zone;
  }) as ZoneEntity[];

  return { tasks, areas, zones };
};
// end reposition all objects from map to illustration image

// reposition from illustration image to map
const getExtentFromCenterZoom = (
  lon: number,
  lat: number,
  zoom: number,
  mapSize: [number, number],
  projection = 'EPSG:3857'
) => {
  // create a dummy view to calculate extent
  const view = new View({
    center: fromLonLat([lon, lat], projection),
    zoom,
    projection
  });

  return view.calculateExtent(mapSize);
};

const imageCoordToMapCoord = (
  imgCoord: [number, number],
  mapExtent: [number, number, number, number],
  imgWidth: number,
  imgHeight: number
): [number, number] => {
  const [minX, minY, maxX, maxY] = mapExtent;

  const sx = (maxX - minX) / imgWidth;
  const sy = (maxY - minY) / imgHeight;

  const x = minX + imgCoord[0] * sx;
  const y = minY + imgCoord[1] * sy;

  return [x, y];
};

export const repositionImageToMap = (
  data: any,
  lon: number,
  lat: number,
  zoom: number,
  mapSize: [number, number],
  imgWidth: number,
  imgHeight: number
) => {
  const mapExtent = getExtentFromCenterZoom(lon, lat, zoom, mapSize) as [
    number,
    number,
    number,
    number
  ];

  const toMap = (c: [number, number]) =>
    imageCoordToMapCoord(c, mapExtent, imgWidth, imgHeight);

  // Tasks
  const tasks = data.tasks?.map((task: any) => {
    if (task.boundary?.geometry?.type === 'Point') {
      task.boundary.geometry.coordinates = toMap(
        task.boundary.geometry.coordinates as [number, number]
      );
    }

    return task;
  }) as TaskEntity[];

  // Areas
  const areas = data.areas?.map((area: any) => {
    const g = area.boundary?.geometry;
    if (g?.type === 'Polygon') {
      g.coordinates = g.coordinates.map((ring: [number, number][]) =>
        ring.map(toMap)
      );
    }

    return area;
  }) as AreaEntity[];

  // Zones
  const zones = data.zones?.map((zone: any) => {
    const g = zone.boundary?.geometry;
    if (g?.type === 'Polygon') {
      g.coordinates = g.coordinates.map((ring: [number, number][]) =>
        ring.map(toMap)
      );
    }

    return zone;
  }) as ZoneEntity[];

  return { zones, areas, tasks };
};
// end reposition from illustration image to map
